import { createContext, useContext, useState } from 'react'

import { AuthContext } from '../../contexts/auth'
import {
  ICompany,
  ICompanyProfileDocuments,
  ICompanyProfileDocumentsCreate,
} from '../../sdk/company/company.types'
import { UserDTO } from '../../sdk/user/user.types'
import { sdk } from '../../services/sdk'

interface MyCompanyDataContext {
  company?: ICompany
  user?: UserDTO
  userType?: string | null
  documents: ICompanyProfileDocuments[]
  expiredDocuments: ICompanyProfileDocuments[]

  actions: {
    getCompany: () => Promise<void>
    getDocuments: (companyId: string) => Promise<void>
    deleteDocument: (id: string) => Promise<void>
    addDocument: (body: ICompanyProfileDocumentsCreate) => Promise<void>
  }
}

interface MyCompanyProviderProps {
  children: React.ReactNode
}

export const MyCompanyContext = createContext({} as MyCompanyDataContext)

export function MyCompanyProvider(props: MyCompanyProviderProps) {
  const [company, setCompany] = useState<ICompany>()
  const [documents, setDocuments] = useState<ICompanyProfileDocuments[]>([])
  const [expiredDocuments, setExpiredDocuments] = useState<
    ICompanyProfileDocuments[]
  >([])

  const { user, userType } = useContext(AuthContext)

  const getCompany = async () => {
    const _response = await sdk.company.getCompanyById(user?.company.id ?? '')
    setCompany(_response)
  }

  const getDocuments = async (companyId: string) => {
    const [_documents, _expiredDocuments] = await Promise.all([
      sdk.company.getDocuments(companyId),
      sdk.company.getExpiredDocuments(),
    ])

    setDocuments(_documents)
    setExpiredDocuments(_expiredDocuments)
  }

  const deleteDocument = async (id: string) => {
    await sdk.company.deleteDocument(id)
    await getDocuments(company?.id ?? '')
  }

  const addDocument = async (body: ICompanyProfileDocumentsCreate) => {
    await sdk.company.updateDocuments(body)
    await getDocuments(company?.id ?? '')
  }

  return (
    <MyCompanyContext.Provider
      value={{
        company,
        user: user?.user,
        userType,
        documents,
        expiredDocuments,

        actions: {
          getCompany,
          getDocuments,
          deleteDocument,
          addDocument,
        },
      }}
    >
      {props.children}
    </MyCompanyContext.Provider>
  )
}
